import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  user: JSON.parse(localStorage.getItem('user')) || null,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  isLoading: false,
  error: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action) => {
      // Handle the API response structure
      const { data } = action.payload;
      console.log(data);
      
      if (data) {
        const { token, user } = data;
        
        // Save token to localStorage
        if (token) {
          localStorage.setItem('token', token);
          state.token = token;
          state.isAuthenticated = true;
        }
        
        // Save user to both localStorage and Redux state
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
          state.user = user;
        }
        
        state.error = null;
      }
    },
    
    // Alternative: Handle direct data passing
    setCredentialsDirect: (state, action) => {
      const { token, user } = action.payload;
      
      // Save token to localStorage
      if (token) {
        localStorage.setItem('token', token);
        state.token = token;
        state.isAuthenticated = !!token;
      }
      
      // Save user to both localStorage and Redux state
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
        state.user = user;
      }
      
      state.error = null;
    },
    
    // Set user data (called from RTK Query success)
    setUser: (state, action) => {
      const { data } = action.payload;
      if (data) {
        localStorage.setItem('user', JSON.stringify(data));
        state.user = data;
      }
      state.isLoading = false;
      state.error = null;
    },
    
    logout: (state) => {
      // Remove from localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Reset state
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      state.isLoading = false;
      
      // Redirect to login (if in browser)
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    },
    
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    updateUser: (state, action) => {
      // Update both localStorage and Redux state
      if (state.user) {
        const updatedUser = { ...state.user, ...action.payload };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        state.user = updatedUser;
      }
    },
    
    // Initialize from localStorage (useful for app startup)
    initializeAuth: (state) => {
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      
      if (token) {
        state.token = token;
        state.isAuthenticated = true;
      }
      
      if (user) {
        try {
          state.user = JSON.parse(user);
        } catch (error) {
          console.error('Error parsing user from localStorage:', error);
          localStorage.removeItem('user');
        }
      }
    },
    
    // Handle token expiration or invalid token
    handleAuthError: (state) => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = 'Authentication failed';
      state.isLoading = false;
    },
  },
});

export const { 
  setCredentials, 
  setCredentialsDirect,
  setUser,
  logout, 
  setLoading,
  setError,
  clearError,
  updateUser,
  initializeAuth,
  handleAuthError
} = authSlice.actions;

// Selectors
export const selectCurrentUser = (state) => state.auth.user;
export const selectToken = (state) => state.auth.token;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthLoading = (state) => state.auth.isLoading;
export const selectAuthError = (state) => state.auth.error;

// Additional selectors for user properties
export const selectUserId = (state) => state.auth.user?.userId;
export const selectUserRole = (state) => state.auth.user?.role;
export const selectUserName = (state) => state.auth.user?.userName;
export const selectCheckPoint = (state) => state.auth.user?.checkPoint;

// Updated helper selector - now checks if user exists in localStorage or state
export const selectShouldFetchUser = (state) => 
  state.auth.isAuthenticated && 
  state.auth.token && 
  !state.auth.user && 
  !state.auth.isLoading &&
  !localStorage.getItem('user');

export default authSlice.reducer;